import { Suspense, useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { Environment } from '@react-three/drei'
import { moodState } from '../../lib/world/mood'
import CrashGuard from '../ui/CrashGuard'

// The image-based light. Back on drei's own preset by Sam's call, after a
// self-hosted gainmap of the same source image came out looking wrong: an
// UltraHDR JPEG rebuilds its range from a tone-mapped base plus a gain
// map, and at this image's content boost (~11.9) that round trip does not
// come back clean. Nine tenths smaller is no bargain if the world lights
// differently.
//
// Known cost, accepted deliberately: the preset does not ship its HDR, it
// fetches it from raw.githubusercontent.com at runtime, and the day
// GitHub rate-limited that fetch the 429 threw inside the render tree and
// the whole world went black for everyone. So the fetch stays, but the
// FAILURE does not: the guard below turns an outage into "no reflections
// for a while" rather than a dead page, which is the version of this risk
// actually worth carrying.

// Day ↔ night light targets. The single directional is the sun by day and
// a faint blue moon by night; ambient keeps the night readable (art piece,
// not a horror game). Warmth tints the daylight; danger cools it slightly.
const SUN_DAY = new THREE.Color('#fff4e0')
const SUN_WARM = new THREE.Color('#ffd9a3')
const SUN_COLD = new THREE.Color('#dfe8ff')
const MOON = new THREE.Color('#8ea6ec')
const AMB_DAY = new THREE.Color('#cfd6e6')
const AMB_NIGHT = new THREE.Color('#3a4478')

// Night brightness — the moon directional + lifted ambient keep the scene
// readable between the lamp pools (art piece, not a horror game). Nudged up
// so night reads clearly (it was too dark), while still feeling like night.
const MOON_INTENSITY = 0.62
const AMB_NIGHT_INTENSITY = 0.5

// Where the sun sits at noon. The director passes its own position so a
// shot can put the light behind him, and passing one pins the sun there;
// the live world lets it travel (see sunAt).
export const SUN_POSITION = [-30, 20, 30]

// The arc, sized off the noon position above so midday still looks like
// the world people know. Radius is the horizontal reach at sunrise and
// sunset, height the lift at noon.
const SUN_RADIUS = 42
const SUN_HEIGHT = 30
// A tilt out of the pure east-west plane, so shadows fall at an angle
// across the streets rather than perfectly along them all day.
const SUN_TILT = 22
// The light never lies flat on the horizon: at zero the shadows stretch
// to infinity and walk straight off the 300-unit shadow camera. A shallow
// floor keeps the long raking golden-hour light without the artefacts.
const MIN_LIFT = 0.2

/**
 * Sun position for a moment in the world's day, written into `out`.
 *
 * dayPhase runs 0 midnight, 0.25 dawn, 0.5 noon, 0.75 dusk. The light
 * crosses the sky through the day and comes back through the night, and
 * one directional does the whole cycle because the colour code below
 * already turns this same light from sun to moon by nightness.
 *
 * The height uses |sin|, so the path is a pendulum rather than a circle,
 * and that choice is the whole design. Mirroring the arc below the
 * horizon is more astronomically correct (the moon rises where the sun
 * set), and it was the first cut, and probing it showed why it cannot
 * ship: the light TELEPORTS the full width of the sky at dawn and again
 * at dusk, swinging every shadow in the world through 180 degrees in one
 * frame, twice a world hour. A pendulum has no seam anywhere. The cost is
 * that the moon travels back the way the sun came, which nobody has ever
 * noticed in any game, against a glitch everybody would.
 */
export function sunAt(dayPhase, out) {
  const p = ((dayPhase % 1) + 1) % 1
  const a = (p - 0.25) * Math.PI * 2       // dawn 0, noon PI/2, dusk PI
  const x = Math.cos(a) * SUN_RADIUS
  const lift = Math.max(MIN_LIFT, Math.abs(Math.sin(a)))
  return out.set(x, lift * SUN_HEIGHT, SUN_TILT)
}

export function Lights({ position = null }) {
    const dirLightRef = useRef()
    const ambientRef = useRef()
    // Reused every frame; a fresh Vector3 at 60fps is garbage for nothing.
    const sunPos = useRef(new THREE.Vector3(...SUN_POSITION))
    const _sun = useRef(new THREE.Color()).current

    useEffect(() => {
        const light = dirLightRef.current
        if (!light) return

        light.castShadow = true
        // 150, not 100: the ortho frustum hangs off the light's own
        // position, and at +/-100 the far west of the map (payphone
        // through the stone and art wall, around x -80) fell outside it,
        // so that whole corner cast nothing while the east looked fine.
        // 2048 over the wider span is ~7px per unit, still clean.
        light.shadow.camera.near = 1
        light.shadow.camera.far = 350
        light.shadow.camera.right = 150
        light.shadow.camera.left = -150
        light.shadow.camera.top = 150
        light.shadow.camera.bottom = -150
        // One standard 2048 shadow map. The old 2x2 TileShadowNode rendered the
        // caster set FOUR times per frame for the same effective resolution
        // (four 1024 tiles); a single 2048 map is the same sharpness for a
        // quarter of the cost. Note TileShadowNode needs a real tiling (>=2),
        // it produces no shadow at 1x1, so we drop the node entirely rather
        // than configure it down.
        light.shadow.mapSize.set(2048, 2048)
        light.shadow.bias = -0.01
        light.shadow.normalBias = 0.05
    }, [])

    // Mood drive — nightness dims + recolours; warmth sets the day light's
    // temperature; overcast flattens (softer sun, lifted ambient ratio).
    useFrame(() => {
        const n = moodState.nightness
        const w = moodState.warmth
        const o = moodState.overcast

        const dir = dirLightRef.current
        if (dir) {
            // The sun travels unless a caller pinned it (the director rig
            // passes a position so a shot can hold its light still). Free:
            // this frame loop already ran, the shadow map already
            // re-renders every frame, and a directional light only cares
            // about the DIRECTION from here to the origin.
            if (!position) {
                // sunPhase, not dayPhase. They are the same number until
                // the town clock has gone unwound long enough for the day
                // to stop turning over, at which point this light holds
                // and the hours carry on without it. Reading dayPhase
                // here would walk the sun across a sky whose colour had
                // already stopped, which is a bug wearing the costume of
                // a feature.
                sunAt(moodState.sunPhase, sunPos.current)
                dir.position.copy(sunPos.current)
            }
            _sun.copy(SUN_COLD).lerp(SUN_WARM, Math.min(1, w * 1.4)).lerp(SUN_DAY, 0.35)
            dir.color.copy(_sun).lerp(MOON, n)
            const dayIntensity = 1.5 * (1 - o * 0.55)
            dir.intensity = dayIntensity * (1 - n) + MOON_INTENSITY * n
        }
        const amb = ambientRef.current
        if (amb) {
            amb.color.copy(AMB_DAY).lerp(AMB_NIGHT, n)
            amb.intensity = (0.25 + o * 0.2) * (1 - n) + AMB_NIGHT_INTENSITY * n
        }
    })

    return (
        <>
            {/* The guard is the whole point of keeping the CDN fetch: a
                429 from GitHub must cost reflections, not the page. The
                tagged boundary renders null on error and the Suspense
                keeps its loading off the rest of the canvas. */}
            <CrashGuard tag="env">
                <Suspense fallback={null}>
                    <Environment preset="city" environmentIntensity={0.45} />
                </Suspense>
            </CrashGuard>
            <ambientLight ref={ambientRef} intensity={0.25} />
            <directionalLight
                ref={dirLightRef}
                position={position || SUN_POSITION}
                intensity={1.5}
                castShadow
            />
        </>
    )
}
